import { useEffect, useRef } from "react";

type OpcoesDeSecao = {
  /**
   * Dispara uma vez só por montagem. Padrão `true`: antes o padrão era o
   * oposto, e nenhum dos chamadores passava a opção — então toda reentrada no
   * viewport contava como uma nova visualização. Uma pessoa que subia e
   * descia a landing gerava `pain_solution_viewed` três vezes no mesmo minuto.
   */
  once?: boolean;
  /**
   * Quanto tempo a seção precisa ficar visível antes de contar. Atravessar a
   * seção não é vê-la.
   */
  dwellMs?: number;
};

export function useTrackSection(onEnter: () => void, options?: OpcoesDeSecao) {
  const { once = true, dwellMs = 0 } = options ?? {};
  const ref = useRef<HTMLElement | null>(null);

  // Guarda o callback mais recente para o efeito não precisar dele nas deps.
  const onEnterRef = useRef(onEnter);
  useEffect(() => {
    onEnterRef.current = onEnter;
  }, [onEnter]);

  const jaDisparou = useRef(false);

  useEffect(() => {
    const alvo = ref.current;
    if (!alvo) return;

    let cronometro: number | undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (once && jaDisparou.current) return;
          if (cronometro !== undefined) return;

          cronometro = window.setTimeout(() => {
            cronometro = undefined;
            jaDisparou.current = true;
            onEnterRef.current();
            if (once) observer.disconnect();
          }, dwellMs);
          return;
        }

        // Saiu da tela antes de cumprir o tempo mínimo: não conta.
        if (cronometro !== undefined) {
          window.clearTimeout(cronometro);
          cronometro = undefined;
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(alvo);

    return () => {
      if (cronometro !== undefined) window.clearTimeout(cronometro);
      observer.disconnect();
    };
  }, [once, dwellMs]);

  return ref;
}
